<template>
  <div class="order-history-content">
    <el-collapse v-model="activeCollapse">
      <!-- 包月方案 -->
      <el-collapse-item :name="1" style="border: none;">
        <template slot="title">
          <div class="collapse-title">
            <i class="el-icon-s-claim" />
            包月方案
          </div>
        </template>
        <div class="collapse-body">
          <div v-if="loading.prepaid" v-loading="loading.prepaid" style="min-height: 100px;"></div>
          <template v-else>
            <div v-if="prepaidOrders.length === 0" class="empty-state">
              <p>暫無包月訂單</p>
            </div>
            <el-table
              v-else
              :data="prepaidOrders"
              :default-sort="{ prop: 'created_at', order: 'descending' }"
              style="width: 100%; cursor: pointer;"
              @row-click="openPrepaidDetail"
              @sort-change="sortPrepaidChange"
            >
              <el-table-column v-slot="{ row }" min-width="180" label="方案詳情">
                <div
                  ><b>{{ row.prepaidTitle }}</b></div
                >
                <div class="text-secondary-light fs-sm">{{ row.sizeTitle }}</div>
                <div class="text-secondary-light fs-sm Jost">NT$ {{ row.total }}</div>
              </el-table-column>
              <el-table-column v-slot="{ row }" width="130" label="次數與期限">
                <div
                  ><b>{{ row.times - (row.used || 0) }} / {{ row.times }}</b></div
                >
                <div class="text-secondary-light fs-sm">{{ row.storeTitles ? row.storeTitles.join(' | ') : '-' }}</div>
              </el-table-column>
              <el-table-column v-slot="{ row }" label="訂單編號" min-width="110" sortable="custom">
                <div class="Jost">#{{ row.orderNo }}</div>
                <div class="text-secondary-light fs-sm">{{ row.created | formatDate('dateTime') }}</div>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="pagination.prepaid.total > 0"
              :current-page.sync="currentPage.prepaid"
              :page-size="pageSize.prepaid"
              :total="pagination.prepaid.total"
              layout="prev, pager, next, jumper"
              background
              style="margin-top: 20px; text-align: center;"
              @current-change="handlePrepaidPageChange"
            />
          </template>
        </div>
      </el-collapse-item>

      <!-- 預約訂單 -->
      <el-collapse-item :name="2" style="border: none;">
        <template slot="title">
          <div class="collapse-title">
            <i class="el-icon-date" />
            預約訂單
          </div>
        </template>
        <div class="collapse-body">
          <div v-if="loading.orders" v-loading="loading.orders" style="min-height: 100px;"></div>
          <template v-else>
            <div v-if="productOrders.length === 0" class="empty-state">
              <p>暫無預約訂單</p>
            </div>
            <el-table
              v-else
              :data="productOrders"
              :default-sort="{ prop: 'created_at', order: 'descending' }"
              style="width: 100%; cursor: pointer;"
              @row-click="openProductDetail"
              @sort-change="sortProductChange"
            >
              <el-table-column v-slot="{ row }" min-width="180" label="服務詳情">
                <div
                  ><b>{{ row.pet ? row.pet.name : '-' }}</b></div
                >
                <div class="text-secondary-light fs-sm" style="margin: 4px 0;">{{ row.productName }}</div>
                <div
                  ><b style="font-size: 12px;">{{ row.params ? row.params[0].title : '-' }}</b></div
                >
              </el-table-column>
              <el-table-column v-slot="{ row }" width="130" label="狀態與金額">
                <div style="margin-bottom: 6px;"
                  ><b>{{ row.status | bookingStatusFilter('text') }}</b></div
                >
                <div style="margin: 6px 0;"
                  ><b class="Jost" style="font-size: 14px;">NT$ {{ formatPrice(row.allActualPayTotal) }}</b></div
                >
                <el-tag :type="getPaymentStatusType(row.paymentStatus)" size="small">
                  {{ row.paymentStatus | paymentStatusMapFilter('text') }}
                </el-tag>
              </el-table-column>
              <el-table-column v-slot="{ row }" label="訂單編號" min-width="110" sortable="custom">
                <div class="Jost">#{{ row.id }}</div>
                <div class="text-secondary-light fs-sm">{{ row.date | formatDate }} {{ row.time }}</div>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="pagination.product.total > 0"
              :current-page.sync="currentPage.product"
              :page-size="pageSize.product"
              :total="pagination.product.total"
              layout="prev, pager, next, jumper"
              background
              style="margin-top: 20px; text-align: center;"
              @current-change="handleProductPageChange"
            />
          </template>
        </div>
      </el-collapse-item>

      <!-- 點數紀錄 -->
      <el-collapse-item v-if="hasDeposit" :name="3" style="border: none;">
        <template slot="title">
          <div class="collapse-title">
            <i class="el-icon-coin" />
            點數紀錄
          </div>
        </template>
        <div class="collapse-body">
          <div v-if="loading.deposit" v-loading="loading.deposit" style="min-height: 100px;"></div>
          <template v-else>
            <div v-if="depositRecords.length === 0" class="empty-state">
              <p>暫無點數紀錄</p>
            </div>
            <el-table
              v-else
              :data="depositRecords"
              :default-sort="{ prop: 'created_at', order: 'descending' }"
              style="width: 100%; cursor: pointer;"
              @row-click="openDepositDetail"
              @sort-change="sortDepositChange"
            >
              <el-table-column v-slot="{ row }" min-width="180" label="點數詳情">
                <div>
                  <b>{{ row.extraInfo && row.extraInfo.price_title ? row.extraInfo.price_title : '儲值點數' }}</b></div
                >
                <div class="text-secondary-light fs-sm">本次餘 {{ row.reduce || 0 }} 點</div>
              </el-table-column>
              <el-table-column v-slot="{ row }" width="130" label="狀態與付款">
                <div style="margin-bottom: 6px;"
                  ><b :class="getDepositTypeClass(row.type)">{{ getDepositTypeText(row.type) }}</b></div
                >
                <div :class="getDepositTypeClass(row.type)" class="fs-sm" style="margin-bottom: 4px;">{{ row.total || 0 }} 點</div>
              </el-table-column>
              <el-table-column v-slot="{ row }" prop="createdAt" label="訂單編號" min-width="110" sortable="custom">
                <div class="Jost">#{{ row.extraInfo ? row.extraInfo.display_order_id : row.id }}</div>
                <div class="text-secondary-light fs-sm">{{ row.createdAt }}</div>
              </el-table-column>
            </el-table>
            <el-pagination
              v-if="pagination.deposit.total > 0"
              :current-page.sync="currentPage.deposit"
              :page-size="pageSize.deposit"
              :total="pagination.deposit.total"
              layout="prev, pager, next, jumper"
              background
              style="margin-top: 20px; text-align: center;"
              @current-change="handleDepositPageChange"
            />
          </template>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import formatDate from '@utils/format-date'
import { orderPaymentMethodFilter, paymentStatusMapFilter, bookingStatusFilter } from '@utils/filter-value-to-text'

export default {
  name: 'OrderHistoryContent',
  filters: {
    formatDate,
    orderPaymentMethodFilter,
    paymentStatusMapFilter,
    bookingStatusFilter,
  },
  props: {
    memberId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      activeCollapse: [1],
      loading: {
        prepaid: false,
        orders: false,
        deposit: false,
      },
      prepaidOrders: [],
      productOrders: [],
      depositRecords: [],
      hasDeposit: false,
      filterData: {
        prepaid: { order_by: 'created_at', order: 'desc', paymentMethod: 'All' },
        product: { order_by: 'created_at', order: 'desc', paymentMethod: 'All', types: ['cosmetic', 'goods'] },
        deposit: { status: '', dateType: 'create', startDate: '', endDate: '' },
      },
      currentPage: {
        prepaid: 1,
        product: 1,
        deposit: 1,
      },
      pageSize: {
        prepaid: 15,
        product: 10,
        deposit: 10,
      },
      pagination: {
        prepaid: { total: 0 },
        product: { total: 0 },
        deposit: { total: 0 },
      },
    }
  },
  created() {
    this.checkHasDeposit()
    this.fetchAllData()
  },
  methods: {
    handlePrepaidPageChange(page) {
      this.currentPage.prepaid = page
      this.fetchPrepaidOrders()
    },
    handleProductPageChange(page) {
      this.currentPage.product = page
      this.fetchProductOrders()
    },
    handleDepositPageChange(page) {
      this.currentPage.deposit = page
      this.fetchDepositRecords()
    },
    checkHasDeposit() {
      this.hasDeposit = this.$hasDeposit && this.$hasDeposit()
    },
    async fetchAllData() {
      await Promise.all([this.fetchPrepaidOrders(), this.fetchProductOrders(), this.hasDeposit ? this.fetchDepositRecords() : Promise.resolve()])
    },
    async fetchPrepaidOrders() {
      if (!this.memberId) return
      this.loading.prepaid = true
      try {
        const { items, total } = await this.$store.dispatch('prepaid/fetchOrders', {
          memberId: this.memberId,
          ...this.filterData.prepaid,
          page: this.currentPage.prepaid,
          per_page: this.pageSize.prepaid,
        })
        this.prepaidOrders = items || []
        this.pagination.prepaid.total = total
      } catch (error) {
        console.error('取得包月方案失敗', error)
        this.prepaidOrders = []
      } finally {
        this.loading.prepaid = false
      }
    },
    async fetchProductOrders() {
      if (!this.memberId) return
      this.loading.orders = true
      try {
        const { items, total } = await this.$store.dispatch('order/fetchList', {
          memberId: this.memberId,
          ...this.filterData.product,
          page: this.currentPage.product,
          limit: this.pageSize.product,
        })

        this.productOrders = items || []
        this.pagination.product.total = total || 0
      } catch (error) {
        console.error('取得預約訂單失敗', error)
        this.productOrders = []
      } finally {
        this.loading.orders = false
      }
    },
    async fetchDepositRecords() {
      if (!this.memberId) return
      this.loading.deposit = true
      try {
        const response = await this.$store.dispatch('deposit/fetchHistoryList', {
          member_id: this.memberId,
          page: 1,
          paginate: 0,
          order: 'desc',
        })

        this.depositRecords = response.items || []
        this.pagination.deposit.total = response.records || 0
      } catch (error) {
        console.error('取得點數紀錄失敗', error)
        this.depositRecords = []
      } finally {
        this.loading.deposit = false
      }
    },
    formatDate(row, column, cellValue) {
      const dateValue = cellValue || row.created || row.created_at
      return dateValue ? formatDate(dateValue, 'dateTime') : '-'
    },
    sortPrepaidChange({ column, prop, order }) {
      if (order) {
        this.filterData.prepaid.order_by = prop
        this.filterData.prepaid.order = order === 'ascending' ? 'asc' : 'desc'
        this.currentPage.prepaid = 1
        this.fetchPrepaidOrders()
      }
    },
    sortProductChange({ column, prop, order }) {
      if (order) {
        this.filterData.product.order_by = prop
        this.filterData.product.order = order === 'ascending' ? 'asc' : 'desc'
        this.currentPage.product = 1
        this.fetchProductOrders()
      }
    },
    sortDepositChange({ column, prop, order }) {
      if (order) {
        // API 一次返回全部資料，在前端排序
        if (prop === 'createdAt') {
          this.depositRecords.sort((a, b) => {
            const aTime = new Date(a.createdAt).getTime()
            const bTime = new Date(b.createdAt).getTime()
            return order === 'ascending' ? aTime - bTime : bTime - aTime
          })
        }
      } else {
        // 取消排序，恢復原始順序
        this.fetchDepositRecords()
      }
    },
    openPrepaidDetail(row) {
      this.$message.info('點擊包月訂單 ID: ' + row.orderNo)
    },
    openProductDetail(row) {
      this.$message.info('點擊預約訂單 ID: ' + row.id)
    },
    openDepositDetail(row) {
      this.$message.info('點擊點數紀錄 ID: ' + (row.extraInfo ? row.extraInfo.display_order_id : row.id))
    },
    getPrepaidStatusType(status) {
      const map = {
        active: 'success',
        expired: 'info',
        used_up: 'warning',
        cancelled: 'danger',
      }
      return map[status] || 'info'
    },
    getPrepaidStatusText(status) {
      const map = {
        active: '有效',
        expired: '已過期',
        used_up: '已用完',
        cancelled: '已取消',
      }
      return map[status] || status || '未知'
    },
    getOrderStatusType(status) {
      const map = {
        PAID: 'success',
        UNPAID: 'warning',
        completed: 'success',
        pending: 'info',
        cancelled: 'danger',
      }
      return map[status] || 'info'
    },
    getOrderStatusText(status) {
      const map = {
        PAID: '已付款',
        UNPAID: '待付款',
        completed: '已完成',
        pending: '待處理',
        cancelled: '已取消',
      }
      return map[status] || status || '未知'
    },
    getDepositTypeClass(type) {
      const map = {
        charge: 'text-success',
        paid: 'text-danger',
        withdraw: 'text-primary',
        refund: 'text-warning',
      }
      return map[type] || ''
    },
    getDepositTypeText(type) {
      const map = {
        charge: '儲值',
        paid: '抵扣',
        withdraw: '結清',
        refund: '退款',
      }
      return map[type] || type || '未知'
    },
    formatPrice(price) {
      if (!price) return '0'
      return Math.round(price).toLocaleString('zh-TW')
    },
    getPaymentStatusType(status) {
      const map = {
        PAID: 'success',
        UNPAID: 'danger',
        REFUNDED: 'warning',
      }
      return map[status] || 'info'
    },
    getBookingStatusType(status) {
      // 0: 取消, 1: 預約, 2: 報到, 3: 完成
      const map = {
        0: 'info',
        1: 'warning',
        2: 'primary',
        3: 'success',
      }
      return map[status] || 'info'
    },
  },
}
</script>

<style lang="scss" scoped>
.order-history-content {
  padding: 0;

  .collapse-title {
    font-size: 16px;
    font-weight: 600;
    width: 100%;
    display: flex;
    align-items: center;

    i {
      margin-right: 8px;
      color: #ea6a2a;
      font-size: 18px;
    }
  }

  .collapse-body {
    padding: 16px;
    padding-top: 0;
  }

  .empty-state {
    text-align: center;
    padding: 20px;
    color: #909399;

    p {
      margin: 0;
    }
  }

  ::v-deep {
    .el-collapse {
      border: none;
      background-color: transparent;

      .el-collapse-item {
        border: none;
        margin-bottom: 12px;

        &__header {
          border: none;
          background-color: #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px !important;
          transition: all 0.3s;

          &:hover {
            background-color: #d1d5db;
          }
        }

        &__wrap {
          border: none;
          background-color: #ffffff;
          border-radius: 0 0 8px 8px;
        }

        &__content {
          padding: 0;
          background-color: #ffffff;
          border-radius: 0 0 8px 8px;
        }
      }
    }

    .el-table {
      font-size: 12px;
      border: none;
      background-color: transparent;

      tr {
        &:hover {
          background-color: #fafafa;
        }
      }

      th {
        background-color: transparent;
        border: none;
        font-weight: 600;
        color: #121931;
        padding: 8px 0;
        border-bottom: 1px solid #e5e7eb;
      }

      td {
        padding: 12px 0;
        border: none;
      }

      .el-table__header-wrapper {
        overflow-x: hidden;
      }

      .el-table__body-wrapper {
        overflow-x: auto;
      }
    }

    .el-tag {
      cursor: default;
    }
  }
}
</style>
